import { useNavigate } from 'react-router'
import { scaleLinear } from 'd3-scale'
import { Skeleton } from '../../../platform/async/Skeleton'
import type { AsyncState } from '../../../platform/async/Async'
import { fixedDecimals } from '../../../platform/fixedDecimals'
import { Money } from '../../../platform/primitives/Money'
import { OverviewWidget } from '../OverviewWidget'
import { STRIP_BARS, type SpentView, type StripBar } from './spentView'

/** The strip always has seven slots, so a bar is a seventh of the tile however few months exist. */
const SLOTS = STRIP_BARS
const SLOT_W = 20
const GAP = 4
const H = 52
/** A zero month still draws a hairline so the strip reads as seven months. */
const FLOOR = 1.5

/**
 * Up to seven months of the server's own `expense`, on a linear scale whose
 * domain is the largest of them. The plot stretches to the tile (`none`), each
 * month takes a seventh of it, and an absent month is a hairline on the left, so the seven
 * slots always span the tile and the current month is last. The bars are `aria-hidden`; the
 * same figures are in the list as text, so no amount lives only in a picture.
 * Month labels are HTML in the type token, not SVG text, which would scale with
 * the stretch.
 */
function Strip({ bars, currency }: { bars: StripBar[]; currency: string }) {
  const tallest = Math.max(1, ...bars.map((b) => b.expenseCents))
  const y = scaleLinear().domain([0, tallest]).range([FLOOR, H])
  // Months before the history begins are empty slots on the left, so the current month is
  // always the last and the seven slots always span the tile.
  const slots: (StripBar | null)[] = [...Array<null>(Math.max(0, SLOTS - bars.length)).fill(null), ...bars]
  return (
    <div className="ov-strip">
      <svg className="ov-strip__plot" viewBox={`0 0 ${SLOTS * SLOT_W} ${H}`} preserveAspectRatio="none" aria-hidden="true" focusable="false">
        {slots.map((b, i) => {
          const h = b === null ? FLOOR : y(b.expenseCents)
          return (
            <rect
              key={b?.start ?? `empty-${i}`}
              className={b?.current ? 'ov-strip__bar ov-strip__bar--current' : 'ov-strip__bar'}
              x={fixedDecimals(i * SLOT_W + GAP / 2, 2)}
              y={fixedDecimals(H - h, 2)}
              width={SLOT_W - GAP}
              height={fixedDecimals(h, 2)}
              data-chart
            />
          )
        })}
      </svg>
      <div className="ov-strip__labels" aria-hidden="true">
        {slots.map((b, i) => (
          <span key={b?.start ?? `empty-${i}`} className="ov-strip__label">
            {b?.label ?? ''}
          </span>
        ))}
      </div>
      <ul className="sr-only" aria-label="Spending by month">
        {bars.map((b) => (
          <li key={b.start} aria-current={b.current ? 'date' : undefined}>
            <span>{`${b.label}: `}</span>
            <Money cents={b.expenseCents} currency={currency} kind="stock" />
            {b.current && <span>{' (this month)'}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Spent this month: the server's month total, last month's total as a plain
 * line (no percentage or arrow — a delta is a subtraction, intake A4), and,
 * for an entitled user, the seven-month strip.
 */
export function SpentWidget({ state, month }: { state: AsyncState<SpentView>; month: string }) {
  const navigate = useNavigate()
  return (
    <OverviewWidget<SpentView>
      title="Spent this month"
      subtitle={month}
      href="/cashflow"
      surface="overview-spent"
      state={state}
      skeleton={
        <>
          <Skeleton height={28} width={150} />
          <Skeleton height={13} width={110} />
          <Skeleton height={44} />
        </>
      }
      empty={{ title: `No spending in ${month} yet`, action: { label: 'See transactions', onAct: () => void navigate('/transactions') } }}
    >
      {({ expenseCents, currency, prior, strip }) => (
        <>
          <Money cents={expenseCents} currency={currency} variant="hero" kind="stock" />
          {prior !== null && (
            <p className="note ov-spent__prior">
              {`${prior.label}: `}
              <Money cents={prior.cents} currency={currency} kind="stock" />
            </p>
          )}
          {strip !== null && strip.length > 0 && <Strip bars={strip} currency={currency} />}
        </>
      )}
    </OverviewWidget>
  )
}
